/**
 * KeyboardManager.ts
 * PHASE 3: Gestionnaire centralisé des raccourcis clavier globaux
 * Gère le focus et évite les conflits avec les champs de saisie
 */
import { logger } from '../utils/ProfessionalLogger';
import { useAppStore } from './StateService';
import { ClipboardService } from './ClipboardService';

type KeyboardHandler = (e: KeyboardEvent) => void | boolean; // true = handled, false = not handled

export class KeyboardManager {
  private static instance: KeyboardManager | null = null;
  private handlers: Map<string, KeyboardHandler[]> = new Map();
  private isEnabled: boolean = true;
  // Référence stable du listener : sans ça, destroy() retirait une fonction
  // `bind()` différente → le listener fuyait et pouvait se dupliquer (raccourcis
  // déclenchés 2×) en cas de ré-initialisation.
  private readonly boundHandleKeyDown = this.handleKeyDown.bind(this);

  private constructor() {
    this.setupDefaultHandlers();
  }

  public static getInstance(): KeyboardManager {
    if (!KeyboardManager.instance) {
      KeyboardManager.instance = new KeyboardManager();
    }
    return KeyboardManager.instance;
  }

  /**
   * NETTOYÉ: Vérifie si un raccourci doit être ignoré (focus dans un champ de saisie)
   * Utilise instanceof pour une vérification plus fiable
   */
  private shouldIgnoreShortcut(e: KeyboardEvent): boolean {
    const target = e.target;

    // Ignorer si on est dans un champ de saisie
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      // Exception: Escape peut toujours être utilisé
      if (e.key === 'Escape') return false;

      // Exception: Ctrl/Cmd + C/V/X/A peuvent être utilisés dans les champs texte
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'a'].includes(e.key.toLowerCase())) {
        return false;
      }

      return true;
    }

    return false;
  }

  /**
   * PHASE 3: Configure les handlers par défaut
   */
  private setupDefaultHandlers(): void {
    // Écouter les événements clavier au niveau window
    window.addEventListener('keydown', this.boundHandleKeyDown, true); // capture phase
  }

  /**
   * PHASE 3: Handler principal pour les touches
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.isEnabled) return;
    if (this.shouldIgnoreShortcut(e)) return;

    const key = this.getKeyString(e);
    const handlers = this.handlers.get(key) || [];

    // Exécuter tous les handlers pour cette touche
    for (const handler of handlers) {
      try {
        const result = handler(e);
        if (result === true) {
          e.preventDefault();
          e.stopPropagation();
          return; // Handler a géré l'événement
        }
      } catch (error) {
        logger.error(`Erreur dans le handler clavier pour ${key}:`, error);
      }
    }

    // Handlers par défaut si aucun handler n'a géré l'événement
    this.handleDefaultShortcuts(e);
  }

  /**
   * PHASE 3: Convertit un événement clavier en string de touche
   */
  private getKeyString(e: KeyboardEvent): string {
    const parts: string[] = [];
    if (e.ctrlKey || e.metaKey) parts.push('mod');
    if (e.shiftKey) parts.push('shift');
    if (e.altKey) parts.push('alt');
    parts.push(e.key.toLowerCase());
    return parts.join('+');
  }

  /**
   * PHASE 3: Handlers par défaut pour les raccourcis standards
   */
  private handleDefaultShortcuts(e: KeyboardEvent): void {
    const store = useAppStore.getState();
    const clipboardService = ClipboardService.getInstance();

    // Delete / Backspace -> Supprimer la sélection
    if ((e.key === 'Delete' || e.key === 'Backspace') && !this.shouldIgnoreShortcut(e)) {
      const selectedIds = store.selection.selectedIds || [];
      if (selectedIds.length > 0) {
        selectedIds.forEach(id => store.deleteShape(id));
        e.preventDefault();
        e.stopPropagation();
        logger.info(`🗑️ Suppression de ${selectedIds.length} élément(s)`);
      }
    }

    // Escape -> Désélectionner
    if (e.key === 'Escape') {
      store.clearSelection();
      e.preventDefault();
      e.stopPropagation();
    }

    // AMÉLIORATION: Raccourcis de navigation
    // Home -> Centrer la vue
    if (e.key === 'Home' && !this.shouldIgnoreShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('canvas-center-view'));
      logger.info('🏠 Vue centrée');
    }

    // Ctrl+0 -> Zoom fit (adapter à l'écran)
    if ((e.ctrlKey || e.metaKey) && e.key === '0' && !this.shouldIgnoreShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('canvas-zoom-fit'));
      logger.info('🔍 Zoom fit');
    }

    // Ctrl+1 -> Zoom 100%
    if ((e.ctrlKey || e.metaKey) && e.key === '1' && !this.shouldIgnoreShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('canvas-zoom-reset'));
      logger.info('🔍 Zoom 100%');
    }

    // Ctrl+Plus -> Zoom in
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === '+' || e.key === '=') &&
      !this.shouldIgnoreShortcut(e)
    ) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('canvas-zoom-in'));
      logger.info('🔍 Zoom in');
    }

    // Ctrl+Minus -> Zoom out
    if ((e.ctrlKey || e.metaKey) && e.key === '-' && !this.shouldIgnoreShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('canvas-zoom-out'));
      logger.info('🔍 Zoom out');
    }

    // Ctrl/Cmd + Z -> Undo
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      if (store.undo) {
        store.undo();
        e.preventDefault();
        e.stopPropagation();
        logger.info('↶ Undo');
      }
    }

    // Ctrl/Cmd + Shift + Z ou Ctrl/Cmd + Y -> Redo
    if (
      ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z') ||
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y')
    ) {
      if (store.redo) {
        store.redo();
        e.preventDefault();
        e.stopPropagation();
        logger.info('↷ Redo');
      }
    }

    // Ctrl/Cmd + C -> Copier
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      const selectedIds = store.selection.selectedIds || [];
      if (selectedIds.length > 0) {
        const shapes = store.shapes.filter(s => selectedIds.includes(s.id));
        clipboardService.copy(shapes);
        e.preventDefault();
        e.stopPropagation();
        logger.info(`📋 Copie de ${shapes.length} élément(s)`);
      }
    }

    // Ctrl/Cmd + V -> Coller
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      clipboardService.paste().then(pastedShapes => {
        if (pastedShapes.length > 0) {
          pastedShapes.forEach(shape => store.addShape(shape));
          logger.info(`📋 Collage de ${pastedShapes.length} élément(s)`);
        }
      });
      e.preventDefault();
      e.stopPropagation();
    }

    // Ctrl/Cmd + X -> Couper
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
      const selectedIds = store.selection.selectedIds || [];
      if (selectedIds.length > 0) {
        const shapes = store.shapes.filter(s => selectedIds.includes(s.id));
        clipboardService.copy(shapes);
        selectedIds.forEach(id => store.deleteShape(id));
        e.preventDefault();
        e.stopPropagation();
        logger.info(`✂️ Coupe de ${shapes.length} élément(s)`);
      }
    }

    // Ctrl/Cmd + A -> Tout sélectionner (sauf si on tape dans un champ texte,
    // où Ctrl+A doit garder son comportement natif de sélection du texte).
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      const inField =
        e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
      if (!inField) {
        const allIds = store.shapes.filter(s => !s.locked && !s.hidden).map(s => s.id);
        if (allIds.length > 0) {
          store.setSelectedNodes(allIds);
          e.preventDefault();
          e.stopPropagation();
          logger.info(`☑️ Tout sélectionné (${allIds.length} élément(s))`);
        }
      }
    }

    // Ctrl/Cmd + D -> Dupliquer
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
      const selectedIds = store.selection.selectedIds || [];
      if (selectedIds.length > 0) {
        const shapes = store.shapes.filter(s => selectedIds.includes(s.id));
        shapes.forEach(shape => {
          const duplicated = {
            ...shape,
            id: `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };
          duplicated.x = (duplicated.x || 0) + 20;
          duplicated.y = (duplicated.y || 0) + 20;
          store.addShape(duplicated);
        });
        e.preventDefault();
        e.stopPropagation();
        logger.info(`📋 Duplication de ${shapes.length} élément(s)`);
      }
    }

    // AMÉLIORATION: Raccourcis contextuels pour les outils (seulement si pas de modificateur)
    if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      const key = e.key.toLowerCase();
      const setActiveTool = store.setActiveTool;

      switch (key) {
        case 'v':
          // Outil Sélection (Move)
          setActiveTool('select');
          e.preventDefault();
          e.stopPropagation();
          logger.info('🎯 Outil Sélection activé');
          return;
        case 't':
          // Outil Texte
          setActiveTool('text');
          e.preventDefault();
          e.stopPropagation();
          logger.info('📝 Outil Texte activé');
          return;
        case 'r':
          // Outil Rectangle
          setActiveTool('rect');
          e.preventDefault();
          e.stopPropagation();
          logger.info('▭ Outil Rectangle activé');
          return;
        case 'c':
          // Outil Cercle
          setActiveTool('circle');
          e.preventDefault();
          e.stopPropagation();
          logger.info('○ Outil Cercle activé');
          return;
        case 'g': {
          // Grouper les éléments sélectionnés
          const selectedIds = store.selection.selectedIds || [];
          if (selectedIds.length >= 2) {
            store.groupShapes(selectedIds);
            e.preventDefault();
            e.stopPropagation();
            logger.info(`📦 ${selectedIds.length} éléments groupés`);
          }
          return;
        }
        case 'u': {
          // Dégrouper l'élément sélectionné
          const primaryId = store.selection.primaryId;
          if (primaryId) {
            const shape = store.shapes.find(s => s.id === primaryId);
            if (shape && shape.type === 'group') {
              store.ungroupShape(primaryId);
              e.preventDefault();
              e.stopPropagation();
              logger.info('📦 Groupe dégroupé');
            }
          }
          return;
        }
        case 'z':
          // Zoom (avec clic pour zoomer, Alt+Z pour dézoomer)
          // Note: Le zoom avec molette est déjà géré ailleurs
          // Ici on peut ajouter un mode zoom temporaire
          if (e.altKey) {
            // Alt+Z = dézoomer
            const currentZoom = store.canvas.zoomLevel;
            store.setZoomLevel(Math.max(0.1, currentZoom - 0.1));
            e.preventDefault();
            e.stopPropagation();
          }
          return;
      }
    }

    // PHASE 3: Flèches -> Déplacer la sélection pixel par pixel
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (arrowKeys.includes(e.key)) {
      const selectedIds = store.selection.selectedIds || [];
      if (selectedIds.length > 0) {
        const step = e.shiftKey ? 10 : 1; // Shift = 10px, sinon 1px
        let deltaX = 0;
        let deltaY = 0;

        switch (e.key) {
          case 'ArrowUp':
            deltaY = -step;
            break;
          case 'ArrowDown':
            deltaY = step;
            break;
          case 'ArrowLeft':
            deltaX = -step;
            break;
          case 'ArrowRight':
            deltaX = step;
            break;
        }

        if (deltaX !== 0 || deltaY !== 0) {
          selectedIds.forEach(id => {
            const shape = store.shapes.find(s => s.id === id);
            if (shape) {
              store.updateShape(id, {
                x: (shape.x || 0) + deltaX,
                y: (shape.y || 0) + deltaY,
              });
            }
          });
          e.preventDefault();
          e.stopPropagation();
        }
      }
    }
  }

  /**
   * PHASE 3: Enregistre un handler personnalisé
   */
  public registerHandler(key: string, handler: KeyboardHandler): () => void {
    if (!this.handlers.has(key)) {
      this.handlers.set(key, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.handlers.get(key)!.push(handler);

    // Retourner une fonction pour désenregistrer
    return () => {
      const handlers = this.handlers.get(key);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  /**
   * PHASE 3: Active ou désactive le gestionnaire
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * PHASE 3: Détruit le gestionnaire (cleanup)
   */
  public destroy(): void {
    window.removeEventListener('keydown', this.boundHandleKeyDown, true);
    this.handlers.clear();
    KeyboardManager.instance = null;
  }
}
